import Im from "immutable";
import React, { useState, useEffect } from "react";

import { ErrorBoundary } from "storybook-dashboard/errors";
import IssuesRAGPanel from "storybook-dashboard/dashboard/ragPanels/issues";
import IndicatorsRAGPanel from "storybook-dashboard/dashboard/ragPanels/indicators";
import IndicatorsBarChart from "storybook-dashboard/dashboard/charts/verticalBar";
import InputValuesTable from "storybook-dashboard/dashboard/project/inputValues";
import IndicatorPerformanceTable from "storybook-dashboard/dashboard/performanceTable";
import DataExplorer from "storybook-dashboard/dashboard/components/dataExplorer";
import DashboardFilters from "storybook-dashboard/dashboard/reportFilters";
import { ReportingPeriodRangePicker } from "storybook-dashboard/dashboard/components/reportingPeriodRangePicker";
import useApi, { updateOldCache } from "storybook-dashboard/utils/fetching";
import IssueAndIndicatorChart from "storybook-dashboard/dashboard/ragPanels/issueAndIndicatorChart";
import { useRagData, getUrl } from "storybook-dashboard/dashboard/ragPanels/utils";
import Octicon from "react-octicon";
import IndicatorsDoughnut from "storybook-dashboard/dashboard/health/widgets/indicatorsDoughnut";
import { ReportSubmittedLine } from "storybook-dashboard/dashboard/health/widgets";
import TipsSpinner from "storybook-dashboard/spinners/tipsSpinner";
import { getLastUpdateFromNow } from "storybook-dashboard/dashboard/health/widgets/utils";
import { BasicSpinner } from "storybook-dashboard/spinners/basicSpinner";

function RefreshButton({ trigger, isRetrying }) {
  return (
    <span
      className="ml-1"
      style={isRetrying ? {} : { cursor: "pointer" }}
      onClick={() => (isRetrying ? null : trigger())}
    >
      {isRetrying ? <BasicSpinner type="grow" /> : <Octicon name="sync" />}
    </span>
  );
}

// Standalone implementation of UpdateCacheButton from storybook-dashboard/dashboard/ragPanels/utils.js
export function UpdateCacheButton(props) {
  let { hide, filters } = props;
  let url = getUrl(props);
  let { lastUpdate, triggerCacheUpdate, isRetrying, usingCached } = useRagData(url, filters);

  // Trigger a cache update if the cache is older than 24 hours.
  useEffect(() => updateOldCache(lastUpdate, triggerCacheUpdate), [lastUpdate]);

  if (hide || !usingCached) return null;

  return (
    <small className="text-muted">
      Last update: {getLastUpdateFromNow(lastUpdate)}{" "}
      <RefreshButton trigger={triggerCacheUpdate} isRetrying={isRetrying} />
    </small>
  );
}

const AlertIndicatorBadge = ({ message }) => {
  return (
    <div className={`col-sm-12 mt-3`}>
      <div className="alert alert-warning text-wrap font-weight-bold" role="alert">
        {message}
      </div>
    </div>
  );
};

const FiltersSection = ({ filters, showFilters, setShowFilters, companyId, setFilters }) => {
  return (
    <>
      <div className="d-flex flex-row justify-content-end mb-3">
        <button onClick={() => setShowFilters(!showFilters)} className="btn btn-sm btn-primary">
          <Octicon className="mr-1" name="settings" /> {!showFilters ? "Show" : "Hide"} filters
        </button>
      </div>
      <div style={showFilters ? undefined : { display: "none" }}>
        <div className="mb-3">
          <ErrorBoundary>
            <ReportingPeriodRangePicker filters={filters} setFilters={setFilters} />
          </ErrorBoundary>
        </div>

        <div className="row">
          <ErrorBoundary>
            <DashboardFilters companyId={companyId} onApply={(values) => setFilters(values)} appliedFilters={filters} />
          </ErrorBoundary>
        </div>
      </div>
    </>
  );
};

function PerformanceDashboardFilteredDataCharts(props) {
  let { companyId, filters } = props;

  let [selectedIssue, setSelectedIssue] = useState(null);
  let [selectedIndicatorIds, setSelectedIndicatorIds] = useState(Im.Map());
  let { data: meta } = useApi("/api/tenant/meta/");
  let indicatorOrder = meta?.getIn(["meta_json", "indicator_order"]);

  const url = getUrl(props);
  const { data, isLoading, isRetrying, isFetching } = useRagData(url, filters);

  const dataLoading = isLoading || isFetching;

  // Reset the selected indicators to none if the issue selection changes
  useEffect(() => {
    setSelectedIndicatorIds(Im.Map());
  }, [selectedIssue]);

  // Get a list of only the selected indicators
  let selectedIndicators = selectedIndicatorIds.toList();

  if (dataLoading) return <TipsSpinner message="Loading dashboard data" timedOutComment="No dashboard data found" />;

  if (!dataLoading && !data?.get("indicators", Im.Map())?.filter((i) => i?.get("value") !== null).size)
    return (
      <ErrorBoundary>
        <AlertIndicatorBadge message="No indicator results. Indicators will populate once sufficient data is received." />
        <ReportSubmittedLine companyId={companyId} />
      </ErrorBoundary>
    );

  return (
    <ErrorBoundary>
      <div className="row gx-3">
        <div className="col-sm-12 col-lg-6 col-xl-5 d-flex flex-column mb-3">
          <div className="mb-3">
            <ErrorBoundary>
              <IndicatorsDoughnut companyId={companyId} />
            </ErrorBoundary>
          </div>
          <ErrorBoundary>
            <ReportSubmittedLine companyId={companyId} />
          </ErrorBoundary>
        </div>

        <IssueAndIndicatorChart
          data={data}
          dataLoading={dataLoading}
          companyId={companyId}
          chartOrder={0}
          key={`performance-block1-issueSummary-${0}-companyId-${companyId}`}
          filters={filters}
          setPreSelectedIssue={setSelectedIssue}
          summary={true}
          chartKey={`performance-block1-issueSummary-${0}-companyId-${companyId}`}
          customStyles={"col-sm-12 col-lg-6 col-xl-7"}
        />
      </div>

      <hr className="my-4 border" />

      <div className="row gx-3">
        {[1, 2, 3].map((index) => {
          return (
            <ErrorBoundary>
              <IssueAndIndicatorChart
                data={data}
                dataLoading={dataLoading}
                isRetrying={isRetrying}
                companyId={companyId}
                chartOrder={index}
                filters={filters}
                preSelectedIssue={selectedIssue}
                key={`performance-block2-single-${index}-companyId-${companyId}`}
                chartKey={`performance-block2-single-${index}-companyId-${companyId}`}
              />
            </ErrorBoundary>
          );
        })}
      </div>

      <div className="row gx-3">
        {[0].map((index) => {
          return (
            <ErrorBoundary>
              <IssueAndIndicatorChart
                data={data}
                dataLoading={dataLoading}
                isRetrying={isRetrying}
                companyId={companyId}
                multipleIndicators={true}
                chartOrder={index}
                filters={filters}
                preSelectedIssue={selectedIssue}
                key={`performance-block3-multiple-${index}-companyId-${companyId}`}
                chartKey={`performance-block3-multiple-${index}-companyId-${companyId}`}
                customStyles={"mb-3 col-sm-12 col-lg-8"}
              />
            </ErrorBoundary>
          );
        })}
        <div className="col-sm-12 col-lg-4">
          {[0, 1, 2].map((index) => {
            return (
              <IssueAndIndicatorChart
                data={data}
                dataLoading={dataLoading}
                isRetrying={isRetrying}
                companyId={companyId}
                statsOnly={true}
                chartOrder={index}
                filters={filters}
                preSelectedIssue={selectedIssue}
                key={`performance-block3-stats-${index}-companyId-${companyId}`}
                chartKey={`performance-block3-stats-${index}-companyId-${companyId}`}
                customStyles={"mb-2"}
              />
            );
          })}
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default function PerformanceDashboard(props) {
  let { companyId } = props;

  let [filters, setFilters] = useState(Im.Map());
  let [showFilters, setShowFilters] = useState(false);

  // Get data for checking and alerts
  const noFilters = Im.Map();
  const url = getUrl(props);
  const { data, isLoading, isRetrying, isFetching } = useRagData(url, noFilters); // No filters for initial load and checks

  const dataLoading = isLoading || isFetching;

  if (!dataLoading && data?.get("issues").isEmpty()) return <AlertIndicatorBadge message="No indicators set up" />;

  return (
    <ErrorBoundary>
      <div className="container-fluid">
        <div className="d-flex flex-row justify-content-end mb-1 mt-n4">
          <UpdateCacheButton companyId={companyId} filters={filters} />
        </div>

        <FiltersSection
          filters={filters}
          setFilters={setFilters}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          companyId={companyId}
        />

        <PerformanceDashboardFilteredDataCharts {...props} filters={filters} />
      </div>
    </ErrorBoundary>
  );
}
